import { Align, Icon, Image, ITableColumn, Justify, Label, Row, Table, TextAlign } from '@busybusy/webapp-react-ui';
import { FlagIcon, SafetyIcon, TimeCardIcon } from 'assets/icons';
import classNames from 'classnames';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import { useOrganization, useTableSorting } from 'hooks';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IMember } from 'types';
import ITimeRange from 'types/TimeRange';
import { fullName } from 'utils/memberUtils';
import './DailySignOffReportTable.scss';
import { SignOffReportItem, useDailySignOffReportTableData } from './useDailySignOffReportTableData';

export interface IDailySignOffReportTableProps {
  className?: string | object;
  member: IMember;
  timeRange: ITimeRange<DateTime>;
  onOpenDetails: (member: IMember, date: DateTime, refreshAction: () => void) => void;
  onOpenEntries: (member: string, date: DateTime) => void;
  scroller?: HTMLElement;
}

const DailySignOffReportTable = (props: IDailySignOffReportTableProps) => {
  const { className, member, timeRange, onOpenDetails, onOpenEntries, scroller } = props;

  const organization = useOrganization();
  const [t] = useTranslation();
  const { data, hasDataLoaded, refreshData } = useDailySignOffReportTableData(timeRange, member.id);
  const { sorted, onSort, sortedBy, sortDirection, sortIsDirty } = useTableSorting(data);
  const classes = classNames('daily-sign-off-report-table', className, {
    'is-loaded': hasDataLoaded,
    'is-loading': !hasDataLoaded,
  });

  function renderDateColumn(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    const date = row.date;

    function handleOpenEntries(event: React.MouseEvent) {
      // prevent row click from getting triggered
      event.stopPropagation();
      event.preventDefault();

      onOpenEntries(member.id, date);
    }

    return (
      <div className="date-cell">
        <IconButton svg={TimeCardIcon} onClick={(event) => handleOpenEntries(event)} tooltipLabel={t('Entries')} />
        <Label className="ml-8">{date.toFormat('ccc, LLL d')}</Label>
      </div>
    );
  }

  function getLastSignature(row: SignOffReportItem) {
    return row.signatures && row.signatures.length > 0 ? row.signatures[row.signatures.length - 1] : undefined;
  }

  function renderSubmitted(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    const lastSignature = getLastSignature(row);

    if (lastSignature) {
      const date = DateTime.fromISO(lastSignature.createdOnLocal);
      return <Label>{date.toFormat('t D')}</Label>;
    }
    return <Label>---</Label>;
  }

  function renderDeviceType(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    const lastSignature = getLastSignature(row);

    if (lastSignature) {
      return <Label>{lastSignature.deviceType}</Label>;
    }
    return <Label>---</Label>;
  }

  function renderAnswer(row: SignOffReportItem, col: ITableColumn<SignOffReportItem>): ReactNode {
    let answer: boolean | null = null;
    let answerText = '---';
    let cellClasses = classNames({
      'sign-off-table-cell': true,
    });
    let resolved = false;
    let icon = FlagIcon;
    let showIcon = false;
    const signatures = row.signatures;

    if (signatures && signatures.length > 0) {
      switch (col.key) {
        case 'injured': {
          answer = _.some(signatures, (sig) => sig.injured === true);
          const injuredResolved =
            !answer || !signatures.some((item) => _.isNil(item.injuredResolvedOn) && item.injured === true);
          cellClasses = classNames({
            'sign-off-table-cell': true,
            'red-text': answer === true && !injuredResolved,
          });
          resolved = answer === true && injuredResolved;
          icon = SafetyIcon;
          showIcon = answer === true;
          break;
        }
        case 'timeAccurate': {
          answer = !_.some(signatures, (sig) => sig.timeAccurate === false);
          const timeAccurateResolved =
            answer || !signatures.some((item) => _.isNil(item.timeAccurateResolvedOn) && item.timeAccurate === false);
          cellClasses = classNames({
            'sign-off-table-cell': true,
            'red-text': answer === false && !timeAccurateResolved,
          });
          resolved = answer === false && timeAccurateResolved;
          showIcon = answer === false;
          break;
        }
        case 'breakPolicyFollowed': {
          answer = !_.some(signatures, (sig) => sig.breakPolicyFollowed === false);
          const breakPolicyresolved =
            answer ||
            !signatures.some(
              (item) => _.isNil(item.breakPolicyFollowedResolvedOn) && item.breakPolicyFollowed === false
            );
          cellClasses = classNames({
            'sign-off-table-cell': true,
            'red-text': answer === false && !breakPolicyresolved,
          });
          resolved = answer === false && breakPolicyresolved;
          showIcon = answer === false;
          break;
        }
      }

      if (answer === true) {
        answerText = t('Yes');
      } else if (answer === false) {
        answerText = t('No');
      }
    }

    return (
      <Label className={cellClasses}>
        <span>{answerText}</span>
        {showIcon && <Icon svg={icon} className={resolved ? 'resolved' : ''} />}
      </Label>
    );
  }

  function renderAccountName(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    const lastSignature = getLastSignature(row);
    return <Label>{lastSignature ? fullName(lastSignature.proxyMember) : '---'}</Label>;
  }

  function renderSignature(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    const lastSignature = getLastSignature(row);
    if (
      !lastSignature ||
      (lastSignature.signatureUrl === null && lastSignature.memberId !== lastSignature.proxyMemberId)
    ) {
      return <Label>{t('---')}</Label>; // signature required but missing
    } else if (
      lastSignature &&
      lastSignature.signatureUrl === null &&
      lastSignature.memberId === lastSignature.proxyMemberId
    ) {
      return <Label>{t('N/A')}</Label>; // signature not needed
    }

    return <Image src={lastSignature!.signatureUrl!} alt="signature" />;
  }

  function renderNotes(row: SignOffReportItem, _col: ITableColumn<SignOffReportItem>): ReactNode {
    let timeIncorrectDescription = null;
    let injuredDescription = null;
    let breakNotFollowedDescription = null;

    row.signatures?.forEach((sig) => {
      if (sig.timeAccurateReason && sig.timeAccurateReason.trim().length > 0) {
        timeIncorrectDescription = (
          <div>
            <Label className="notes-label m-0">{t('Why was your time incorrect?')}</Label>
            <p className="m-0">{sig.timeAccurateReason}</p>
          </div>
        );
      }

      if (sig.injuryDescription && sig.injuryDescription.trim().length > 0) {
        injuredDescription = (
          <div>
            <Label className="notes-label m-0">{t('Describe your injury')}</Label>
            <p className="m-0">{sig.injuryDescription}</p>
          </div>
        );
      }

      if (sig.breakPolicyFollowedReason && sig.breakPolicyFollowedReason.trim().length > 0) {
        breakNotFollowedDescription = (
          <div>
            <Label className="notes-label m-0">{t("Why didn't you take your breaks?")}</Label>
            <p className="m-0">{sig.breakPolicyFollowedReason}</p>
          </div>
        );
      }
    });

    return (
      <div>
        {timeIncorrectDescription ? timeIncorrectDescription : null}
        {injuredDescription ? injuredDescription : null}
        {breakNotFollowedDescription ? breakNotFollowedDescription : null}
      </div>
    );
  }

  function renderEmptyState(): ReactNode {
    return (
      <Row justify={Justify.CENTER} align={Align.CENTER} className="empty-sign-off-state">
        {hasDataLoaded ? t('There is no sign-off data for this date range.') : undefined}
      </Row>
    );
  }

  function getColumns(): Array<ITableColumn<SignOffReportItem>> {
    const signOffAnswerColumns = Array<ITableColumn<SignOffReportItem>>();

    // only include the columns if the setting is turned on in the organization
    if (organization.timeAccuracy) {
      signOffAnswerColumns.push({
        cell: renderAnswer,
        key: 'timeAccurate',
        sort: true,
        title: t('Time Acc.'),
        align: TextAlign.LEFT,
        size: '120px',
      });
    }
    if (organization.breakPolicy) {
      signOffAnswerColumns.push({
        cell: renderAnswer,
        key: 'breakPolicyFollowed',
        sort: true,
        title: t('Break Comp.'),
        align: TextAlign.LEFT,
        size: '130px',
      });
    }
    if (organization.safetySignature) {
      signOffAnswerColumns.push({
        cell: renderAnswer,
        key: 'injured',
        sort: true,
        title: t('Injured'),
        align: TextAlign.LEFT,
        size: '120px',
      });
    }

    return [
      {
        cell: renderDateColumn,
        key: 'startTime',
        sort: true,
        title: t('Date'),
        align: TextAlign.CENTER,
        size: '180px',
      },
      ...signOffAnswerColumns,
      {
        cell: renderNotes,
        key: 'notes',
        sort: true,
        title: t('Employee Notes'),
        align: TextAlign.LEFT,
        size: '400px',
        cellClassName: 'employee-notes align-left p-4',
      },
      {
        cell: renderSignature,
        key: 'signatureUrl',
        sort: true,
        title: t('Signature'),
        align: TextAlign.LEFT,
        size: '200px',
      },
      {
        cell: renderSubmitted,
        key: 'createdOnLocal',
        sort: true,
        title: t('Submitted'),
        align: TextAlign.LEFT,
        size: '200px',
      },
      {
        cell: renderDeviceType,
        key: 'deviceType',
        sort: true,
        title: t('Device'),
        align: TextAlign.LEFT,
        size: '200px',
      },
      {
        cell: renderAccountName,
        key: 'accountName',
        sort: true,
        title: t('Account'),
        align: TextAlign.LEFT,
        size: '200px',
      },
    ];
  }

  function handleRowClick(row: SignOffReportItem) {
    onOpenDetails(member, row.date, refreshData);
  }

  return (
    <Table
      className={classes}
      cols={getColumns()}
      data={sorted}
      sortDir={sortDirection}
      sortBy={sortedBy}
      strokeCols={true}
      onSort={onSort}
      sortIsDirty={sortIsDirty}
      header="standard"
      emptyTemplate={renderEmptyState()}
      scroller={scroller}
      onRowClick={handleRowClick}
    />
  );
};

export default DailySignOffReportTable;
